import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { PeekInsideSlide } from '../data/peekInsideSlides';
type PageLightboxProps = {
  pages: PeekInsideSlide[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function PageLightbox({
  pages,
  index,
  onClose,
  onChange
}: PageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = index !== null;
  const prev = () => {
    if (index === null) return;
    onChange((index - 1 + pages.length) % pages.length);
  };
  const next = () => {
    if (index === null) return;
    onChange((index + 1) % pages.length);
  };
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);
  const page = index !== null ? pages[index] : null;
  return <AnimatePresence data-id="element-126" data-mp="hfzs6igzzw3t">
      {open && page && <motion.div role="dialog" aria-modal="true" aria-label={page.caption} className="fixed inset-0 z-[60] flex items-center justify-center bg-plum-deep/90 px-4 py-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} onClick={onClose} data-id="element-127" data-mp="mge2k762ni50">
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-128" data-mp="2seq8rvv02xh">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-129" data-mp="1av2df1g9d0mr" />
          </button>

          <button type="button" onClick={e => {
        e.stopPropagation();
        prev();
      }} aria-label="Previous page" className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold sm:left-6" data-id="element-130" data-mp="1ax6l7uha2jsb">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-131" data-mp="27ji636enzq43" />
          </button>

          <motion.figure className="flex max-h-full flex-col items-center" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease
      }} onClick={e => e.stopPropagation()} data-id="element-132" data-mp="23ipw2axyxh6r">
            <img src={page.src} alt={page.alt} className="max-h-[82vh] w-auto max-w-[80vw] rounded-[12px] border border-gold/40 bg-plum-deep object-contain shadow-cover" data-id="element-133" data-mp="2177a770d7547" />
            <figcaption className="mt-3 text-center text-sm text-white/80" data-id="element-134" data-mp="2yt4m2lk8oc76">
              {page.caption}
              <span className="ml-2 text-white/50" data-id="element-135" data-mp="2k4gu0p9lqklz">
                {index! + 1} / {pages.length}
              </span>
            </figcaption>
          </motion.figure>

          <button type="button" onClick={e => {
        e.stopPropagation();
        next();
      }} aria-label="Next page" className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold sm:right-6" data-id="element-136" data-mp="3cscnzm7sdyf2">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-137" data-mp="1jx3li19qun9j" />
          </button>
        </motion.div>}
    </AnimatePresence>;
}